import type { DeskState, Posting, ProtestRules } from "@/api/desk";
export function deskActions(
  roles: string[],
  posting: Posting | null,
  online: boolean,
  fresh: boolean,
  current = true,
) {
  const has = (...wanted: string[]) => online && fresh && roles.some((r) => wanted.includes(r));
  const desk = has("admin", "head_judge", "chief_judge", "chief_scorer");
  const mutable = !!posting && current && !posting.correction_opened_at;
  return {
    post: desk,
    file: desk,
    decide: has("admin", "chief_judge"),
    override: has("admin", "chief_judge"),
    panel: mutable && !posting?.panel_approved_at && has("admin", "head_judge"),
    signChiefJudge: mutable && !posting?.signed_chief_judge_at && has("admin", "chief_judge"),
    signChiefScorer: mutable && !posting?.signed_chief_scorer_at && has("admin", "chief_scorer"),
    correct: mutable && has("admin", "chief_judge", "chief_scorer"),
  };
}
export function safeAttachment(value: string): string | null {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}
export function deadlineLabel(value: string | null, now = Date.now()) {
  if (!value) return "No deadline recorded";
  const seconds = Math.ceil((Date.parse(value) - now) / 1000);
  if (!Number.isFinite(seconds)) return "Deadline unavailable";
  return seconds <= 0
    ? "Window closed"
    : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")} remaining`;
}
export function correctionWindowClosed(posting: Posting, rules: ProtestRules, now = Date.now()) {
  if (rules.correction_window_minutes == null) return false;
  const from =
    rules.correction_window_from === "official" ? posting.official_at : posting.posted_at;
  return from ? now >= Date.parse(from) + rules.correction_window_minutes * 60000 : false;
}
export const kindName: Record<string, string> = {
  protest: "Score protest",
  technical_incident: "Rule non-compliance",
  correction: "Calculation correction",
  review_request: "Review request",
};
export const roleName: Record<string, string> = {
  rider: "Rider",
  team_rep: "Team representative",
  any: "Other filer",
};
export function money(cents: number | null | undefined, currency: string | null | undefined) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "USD",
  }).format((cents ?? 0) / 100);
}
export function canOpenFiling(state: DeskState, currentRules: ProtestRules | null) {
  const permits = (rules: ProtestRules | undefined | null) =>
    !!rules?.allowed && !!rules.kinds.length && !!rules.filers.length;
  return (
    state.eligible_scopes.some((scope) => scope.heats.length > 0) &&
    (permits(currentRules) ||
      state.postings.some(
        (posting) =>
          permits(posting.snapshot?.protest_config) &&
          !state.postings.some(
            (other) =>
              other.division_id === posting.division_id &&
              other.heat_id === posting.heat_id &&
              other.posting_no > posting.posting_no,
          ) &&
          state.eligible_scopes.some(
            (scope) =>
              scope.division_id === posting.division_id &&
              scope.heats.some((heat) => !posting.heat_id || heat.id === posting.heat_id),
          ),
      ))
  );
}
export function timestamp(value: string | null, timeZone: string) {
  if (!value) return "Not recorded";
  try {
    if (!timeZone) throw new RangeError("Missing competition time zone");
    return new Intl.DateTimeFormat(undefined, {
      timeZone,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(new Date(value));
  } catch {
    return "Time unavailable — check the competition time zone";
  }
}
